/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 * Campaigns Table component
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.12.0
 */

import { useCallback, useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { renderDropFilterButton } from '@components/common/PageTitle/PageTitle';
import { makeSelectPageCampaignsTranslation } from '@modules/internationalization/intSelectors';
import { initialise, Table, useTableFilterApi, useTableListApi, useTableMetaApi } from '@modules/table';
import { tableIsErrorSelector, tableIsLoadingSelector } from '@modules/table/selectors';
import { stringFormatParams } from '@utils/stringFormat';
import { atou } from '@utils/URIEncode';

import { makeSelectCampaignsAccountFilter, makeSelectCampaignsSubscriberFilter } from '../../selectors';
import { campaignsApi } from '../../store';
import { ECampaignsType } from '../../types';
import { CampaignsTableStyled } from '../styled/CampaignsTableStyled';
import { CampaignsTableColumns } from './CampaignsTableColumns';
import { CampaignsTableAccountInitialise, CampaignsTableSubscriberInitialise } from './CampaignsTableInitialise';

export interface ICampaignsTableProps {
    type: ECampaignsType;
}

const CampaignsTable = ({ type }: ICampaignsTableProps) => {

    const dispatch = useDispatch();

    const translationCampaigns = useSelector(makeSelectPageCampaignsTranslation());

    const filterAccount = useSelector(
        makeSelectCampaignsAccountFilter(CampaignsTableAccountInitialise.meta?.url));
    const filterSubscriber = useSelector(
        makeSelectCampaignsSubscriberFilter(CampaignsTableSubscriberInitialise.meta?.url));

    useEffect(() => {
        /* Конфигурация таблицы для запроса данных */
        const config = {
            [ECampaignsType.account]: CampaignsTableAccountInitialise,
            [ECampaignsType.subscriber]: CampaignsTableSubscriberInitialise,
        }[type];

        dispatch(initialise(config));
    }, [type, dispatch]);

    /* Table Meta  */
    const config = useMemo(() => CampaignsTableColumns(translationCampaigns), [translationCampaigns]);
    const { columns } = useTableMetaApi(config);

    /* Table List */
    const { data, infiniteLoader } = useTableListApi();

    /* Table Filter */
    const { drop } = useTableFilterApi();

    /* Table Statuses  */
    const isLoading = useSelector(tableIsLoadingSelector);
    const isError = useSelector(tableIsErrorSelector);

    const getPrefilterValue = useCallback(() =>
            type === ECampaignsType.account ? filterAccount : filterSubscriber,
        [type, filterAccount, filterSubscriber]);

    const updateCount = useCallback(() => {
        if (type === ECampaignsType.account) {
            dispatch(campaignsApi.endpoints.getAccountCount.initiate({ from: 0, size: 1 }, { forceRefetch: true }))
        } else {
            dispatch(campaignsApi.endpoints.getSubscriberCount.initiate({ from: 0, size: 1 }, { forceRefetch: true }))
        }
    }, [type, dispatch]);

    const renderPrefilterInfo = useCallback((value: string) => value && (
        <div className="campaign-table__prefilter">
            <div className="prefilter__value">
                {stringFormatParams(translationCampaigns.forTypeOnly[type].title.prefilterLabel, {
                    value: atou(value),
                })}
            </div>
            {
                renderDropFilterButton({
                    className: 'prefilter__drop-filter',
                    value: translationCampaigns.title.dropFilters,
                    onClick: () => drop(updateCount),
                })
            }
        </div>
    ), [drop, type, updateCount, translationCampaigns]);


    if (!columns.length) return null;

    return (
        <CampaignsTableStyled>
            {renderPrefilterInfo(getPrefilterValue())}
            <Table
                isRowHeightAuto
                columns={columns}
                data={data}
                dataName="campaigns"
                dataNameRow="id"
                infiniteScroll={infiniteLoader}
                isCompact={false}
                isError={isError}
                isLoading={isLoading}
            />
        </CampaignsTableStyled>
    );
};


export default CampaignsTable;
